import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";

import Typography from "../../../typography/typography";
import { useLanguage } from "../../../../hooks";
import { ArrowDown } from "../../../icons";

interface ILanguage {
  label: string;
  code: string;
  direction: string;
}

const createEnglishFallback = (): ILanguage => ({
  code: "en",
  label: "English",
  direction: "ltr",
});

export function LanguageSwitch() {
  const { t } = useTranslation();
  const {
    languages,
    currentLanguage,
    changeLanguage,
    isLoading,
    error: languageContextError,
    isFallbackActive,
    fetchLanguages,
  } = useLanguage();

  const [localLanguageError, setLocalLanguageError] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const isMenuOpen = Boolean(anchorEl);
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const getLanguagesForDropdown = (): ILanguage[] => {
    if (!languages || languages.length === 0) {
      return [createEnglishFallback()];
    }
    const hasEnglish = languages.some((lang: ILanguage) => lang.code === "en");
    if (!hasEnglish) {
      return [createEnglishFallback(), ...languages];
    }
    return languages;
  };

  const getCurrentLanguageDisplay = (): string => {
    if (!currentLanguage) return "EN";
    return currentLanguage.code.toUpperCase();
  };

  const onChangeLang = (lang_code: string) => {
    setLocalLanguageError(null);
    handleMenuClose();

    if (!languages || languages.length === 0) {
      const errorMsg = t("header.language_not_available") || "Languages not available";
      setLocalLanguageError(errorMsg);
      return;
    }

    const languageExists = languages.some((lang: ILanguage) => lang.code === lang_code);
    if (languageExists) {
      try {
        changeLanguage(lang_code);
      } catch (error) {
        setLocalLanguageError(error instanceof Error ? error.message : "Failed to change language");
      }
    } else {
      const errorMsg =
        t("header.language_not_found", { language: lang_code }) ||
        `Selected language "${lang_code}" is not available`;
      setLocalLanguageError(errorMsg);
    }
  };

  const handleRetryLanguages = async () => {
    setLocalLanguageError(null);
    try {
      await fetchLanguages();
    } catch (error) {
      setLocalLanguageError("Failed to reload languages");
    }
  };

  const availableLanguages = getLanguagesForDropdown();

  if (isLoading) {
    return (
      <Box className="language-chip language-chip-loading">
        <span>{t("header.language_loading") || "Loading..."}</span>
      </Box>
    );
  }

  return (
    <Box className="language-selector-wrapper">
      <button
        className={`hdr-lang-btn ${isFallbackActive || languageContextError ? "language-fallback-active" : ""}`}
        type="button"
        onClick={handleMenuOpen}
      >
        <span className="lang-text">{getCurrentLanguageDisplay()}</span>
        <ArrowDown width={14} height={14} />
      </button>
      
      <Menu
        className="main-profile-menu"
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {availableLanguages.map(({ code, label }: ILanguage) => (
          <MenuItem key={code} onClick={() => onChangeLang(code)}>
            <Typography type="s4" weight="medium" color={currentLanguage?.code === code ? "theme.primary.1000" : "theme.secondary.1000"}>
              {label}
            </Typography>
          </MenuItem>
        ))}

        {languageContextError && (
          <MenuItem onClick={handleRetryLanguages} style={{ fontStyle: "italic" }}>
            <Typography type="s4" weight="medium" color="theme.secondary.500">
              {t("header.retry_languages") || "Retry loading languages"}
            </Typography>
          </MenuItem>
        )}
      </Menu>
      
      {localLanguageError && (
        <Typography type="s5" color="theme.error.600" style={{ position: "absolute", bottom: "-15px", whiteSpace: "nowrap" }}>
          {localLanguageError}
        </Typography>
      )}
    </Box>
  );
}

export default LanguageSwitch;